import { AppActions } from 'actions';
import { EventMessage } from 'contexts/WebSocketContext';
import Immutable, { List, Map, OrderedMap, Record } from 'immutable';
import isString from 'lodash/isString';
import { BroadcastPayload } from 'records/broadcast';
import { Comment, CommentMap } from 'records/Comment';
import { ConnectedAccount, ConnectedAccountId } from 'records/ConnectedAccount';
import { Doc, DocId } from 'records/Doc';
import { Eula } from 'records/Eula';
import { ActiveExport, ActiveExportMap } from 'records/Export';
import {
  Interaction,
  InteractionConstructor,
  InteractionKey,
  InteractionsAsMap,
} from 'records/interactions';
import { License } from 'records/License';
import { LinkMap } from 'records/Link';
import { MarkupId, MarkupMap } from 'records/Markup';
import { ModalState } from 'records/ModalState';
import { NewRFI } from 'records/NewRFI';
import { Page, PageId } from 'records/Page';
import { PageReadyForReview } from 'records/PageReadyForReview';
import { ProcessingStatus } from 'records/ProcessingStatus';
import { Project, ProjectId } from 'records/Project';
import { ProjectGroup } from 'records/ProjectGroup';
import { PublishRequest, PublishRequestMap } from 'records/PublishRequest';
import { Punch, PunchId } from 'records/Punch';
import { PunchQuery } from 'records/PunchQuery';
import { RFI, RFIId } from 'records/RFI';
import { RFIQuery } from 'records/RFIQuery';
import { Role, Roles } from 'records/Role';
import { Sheet, SheetId, SheetMap } from 'records/Sheet';
import { SpaceMap } from 'records/Space';
import {
  ApprovedFileId,
  SelectedApprovedFiles,
  Submittal,
  SubmittalId,
} from 'records/Submittal';
import {
  SubmittalCategory,
  SubmittalCategoryList,
} from 'records/SubmittalCategory';
import { SubmittalStatus, SubmittalStatusList } from 'records/SubmittalStatus';
import { BatchUploadProgress } from 'records/UploadProgress';
import { User, UserId } from 'records/User';
import { EMPTY, Observable } from 'rxjs';
import { ProjectSettings } from 'records/ProjectSettings';
import { Breadcrumbs } from './Breadcrumbs';
import { GeoLocation } from './GeoLocation';
import { IntegrationsDrive } from './IntegrationsDrive';
import { IntegrationsFolder } from './IntegrationsFolder';
import { IntegrationsSelectedItem } from './IntegrationsSelectedItem';
import { MappedSheetQuery } from './MappedSheetQuery';
import { MappedSheetVersion } from './MappedSheetVersion';
import { ProjectDrive } from './ProjectDrive';
import { ProjectDriveFolderItems } from './ProjectDriveFolderItems';
import { ProjectDriveSelectedItem } from './ProjectDriveSelectedItem';
import { SheetQuery } from './SheetQuery';
import { SheetVersion } from './SheetVersion';
import { StudioProject } from './StudioProject';
import { StudioProjectFileData } from './StudioProjectFile';
import { StudioProjectFolder } from './StudioProjectFolder';
import { SubmittalQuery } from './SubmittalQuery';
import {
  SubmittalReviewerResponse,
  SubmittalReviewerResponseList,
} from './SubmittalReviewerResponse';
import { UserQuery } from './UserQuery';

export type WrappedAppActions = {
  [Action in keyof AppActions]: AppActions[Action] extends (
    params: AppStateActionParams,
    ...args: infer Args
  ) => infer R
    ? (...args: Args) => Promise<R>
    : never;
};

export interface AppStateProps {
  activeExports: ActiveExportMap;
  activeUploads: OrderedMap<string, BatchUploadProgress>;
  breadcrumbsItems: Breadcrumbs;
  canPurchase: boolean;
  connectedAccounts: List<ConnectedAccount>;
  documents: OrderedMap<DocId, Doc>;
  eula: Eula;
  focusedMarkup: MarkupId;
  geoLocation: GeoLocation;
  groupsByMember: Map<string, List<ProjectGroup>>;
  hasActiveSubscription: boolean;
  integrationsConnectedAccountId: string;
  integrationsCurrentDriveId: string;
  integrationsCurrentFolder: IntegrationsFolder;
  integrationsDrives: OrderedMap<ConnectedAccountId, List<IntegrationsDrive>>;
  integrationsFolderExportData: IntegrationsSelectedItem | null;
  integrationsSelectedItems: List<IntegrationsSelectedItem>;
  interactions: List<Interaction>;
  links: OrderedMap<PageId, LinkMap>;
  mappedSheetQuery: MappedSheetQuery;
  mappedSheetVersions: OrderedMap<PageId, MappedSheetVersion>;
  markups: OrderedMap<PageId, MarkupMap>;
  modals: List<ModalState>;
  pages: OrderedMap<PageId, Page>;
  pagesReadyForReview: OrderedMap<PageId, PageReadyForReview>;
  pendoInitialized: boolean;
  processingStatuses: OrderedMap<string, ProcessingStatus>;
  projectDriveCurrentItems: ProjectDriveFolderItems;
  projectDriveSelectedItems: List<ProjectDriveSelectedItem>;
  projectDrives: List<ProjectDrive>;
  projectGroups: List<ProjectGroup>;
  projects: OrderedMap<ProjectId, Project>;
  publishRequests: PublishRequestMap;
  punchComments: CommentMap;
  punchQuery: PunchQuery;
  punchTableQuery: PunchQuery;
  punchSearch: string;
  punches: OrderedMap<PunchId, Punch>;
  queriedMappedSheets: List<SheetId>;
  queriedPunches: List<PunchId>;
  queriedRFIGroups: OrderedMap<string, List<RFI>>;
  queriedRFIs: List<RFIId>;
  queriedSheets: List<SheetId>;
  queriedSubmittals: List<SubmittalId>;
  queriedUsers: List<UserId>;
  recentUploads: OrderedMap<string, BatchUploadProgress>;
  registeredEvents: Immutable.Map<string, BroadcastPayload>;
  rfiQuery: RFIQuery;
  rfiTableQuery: RFIQuery;
  rfis: OrderedMap<RFIId, RFI>;
  roles: Immutable.Map<UserId, Role>;
  selectFolderCurrentId: string;
  selectedApprovedFiles: SelectedApprovedFiles;
  selectedPages: Immutable.Set<PageId>;
  selectedSheetVersions: SheetVersion[];
  selectedProject: ProjectId;
  selectedPublishRequests: Immutable.Set<PageId>;
  selectedPunches: Immutable.Set<PunchId>;
  selectedRFIs: Immutable.Set<RFIId>;
  selectedSheet: SheetId;
  selectedSubmittals: Immutable.Set<SubmittalId>;
  selectedUsers: List<User>;
  serialnumber: string;
  settings: ProjectSettings;
  sheetQuery: SheetQuery;
  sheets: SheetMap;
  spaces: OrderedMap<PageId, SpaceMap>;
  studioCurrentFolder: StudioProjectFolder;
  studioCurrentFolderId: string;
  studioCurrentFolderName: string;
  studioCurrentProjectId: string;
  studioExportFolder: StudioProjectFolder | null;
  studioProjects: Immutable.List<StudioProject>;
  studioSelectedFiles: StudioProjectFileData[];
  studioSelectedFolders: StudioProjectFolder[];
  submittalCategories: SubmittalCategoryList;
  submittalQuery: SubmittalQuery;
  submittalReviewerResponses: SubmittalReviewerResponseList;
  submittalStatuses: SubmittalStatusList;
  submittals: OrderedMap<SubmittalId, Submittal>;
  tosLink: string;
  unsavedChanges: boolean;
  uploadCancelationRequests: List<DocId>;
  userLicense: License;
  userQuery: UserQuery;
  users: OrderedMap<UserId, User>;
  viewRFI: NewRFI;
  viewEdittedRFI: NewRFI;
  visibleMappedSheetVersions: Immutable.Set<PageId>;
  websocketObservable: Observable<EventMessage>;
}

export type AppStateFunc = () => AppState;

export interface AppStateActionParams {
  state: () => AppState;
  actions: WrappedAppActions;
  name: string;
}

const defaultValues: AppStateProps = {
  activeExports: OrderedMap<string, ActiveExport>(),
  activeUploads: OrderedMap<string, BatchUploadProgress>(),
  breadcrumbsItems: new Breadcrumbs(),
  canPurchase: false,
  connectedAccounts: Immutable.List<ConnectedAccount>(),
  documents: Immutable.OrderedMap<DocId, Doc>(),
  eula: new Eula(),
  focusedMarkup: '',
  geoLocation: new GeoLocation(),
  groupsByMember: Map<string, List<ProjectGroup>>(),
  hasActiveSubscription: false,
  integrationsConnectedAccountId: '',
  integrationsCurrentDriveId: '',
  integrationsCurrentFolder: new IntegrationsFolder(),
  integrationsDrives: OrderedMap<ConnectedAccountId, List<IntegrationsDrive>>(),
  integrationsFolderExportData: new IntegrationsSelectedItem(),
  integrationsSelectedItems: List<IntegrationsSelectedItem>(),
  interactions: Immutable.List<Interaction>(),
  links: OrderedMap<PageId, LinkMap>(),
  mappedSheetQuery: new MappedSheetQuery(),
  mappedSheetVersions: OrderedMap<PageId, MappedSheetVersion>(),
  markups: OrderedMap<SheetId, MarkupMap>(),
  modals: Immutable.List<ModalState>(),
  pages: Immutable.OrderedMap<SheetId, Page>(),
  pagesReadyForReview: Immutable.OrderedMap<PageId, PageReadyForReview>(),
  pendoInitialized: false,
  processingStatuses: Immutable.OrderedMap<string, ProcessingStatus>(),
  projectDriveCurrentItems: new ProjectDriveFolderItems(),
  projectDriveSelectedItems: List<ProjectDriveSelectedItem>(),
  projectDrives: List<ProjectDrive>(),
  projectGroups: List<ProjectGroup>(),
  projects: Immutable.OrderedMap<ProjectId, Project>(),
  publishRequests: Immutable.OrderedMap<PageId, PublishRequest>(),
  punchComments: Immutable.OrderedMap<string, Comment>(),
  punchQuery: new PunchQuery(),
  punchTableQuery: new PunchQuery(),
  punchSearch: '',
  punches: Immutable.OrderedMap<PunchId, Punch>(),
  queriedMappedSheets: Immutable.List<SheetId>(),
  queriedPunches: Immutable.List<PunchId>(),
  queriedRFIGroups: OrderedMap<string, List<RFI>>(),
  queriedRFIs: Immutable.List<RFIId>(),
  queriedSheets: Immutable.List<SheetId>(),
  queriedSubmittals: List<SubmittalId>(),
  queriedUsers: Immutable.List<UserId>(),
  recentUploads: OrderedMap<string, BatchUploadProgress>(),
  registeredEvents: Immutable.Map<string, BroadcastPayload>(),
  rfiQuery: new RFIQuery(),
  rfiTableQuery: new RFIQuery(),
  rfis: OrderedMap<RFIId, RFI>(),
  roles: Immutable.Map<UserId, Role>(),
  selectFolderCurrentId: '',
  selectedApprovedFiles: Immutable.Set<ApprovedFileId>(),
  selectedPages: Immutable.Set<PageId>(),
  selectedSheetVersions: [],
  selectedProject: '',
  selectedPublishRequests: Immutable.Set<PageId>(),
  selectedPunches: Immutable.Set<PunchId>(),
  selectedRFIs: Immutable.Set<RFIId>(),
  selectedSheet: '',
  selectedSubmittals: Immutable.Set<SubmittalId>(),
  selectedUsers: Immutable.List<User>(),
  serialnumber: '',
  settings: new ProjectSettings(),
  sheetQuery: new SheetQuery(),
  sheets: Immutable.OrderedMap<SheetId, Sheet>(),
  spaces: OrderedMap<SheetId, SpaceMap>(),
  studioCurrentFolder: new StudioProjectFolder(),
  studioCurrentFolderId: '',
  studioCurrentFolderName: '',
  studioCurrentProjectId: '',
  studioExportFolder: null,
  studioProjects: Immutable.List<StudioProject>(),
  studioSelectedFiles: [],
  studioSelectedFolders: [],
  submittalCategories: Immutable.List<SubmittalCategory>(),
  submittalQuery: new SubmittalQuery(),
  submittalReviewerResponses: Immutable.List<SubmittalReviewerResponse>(),
  submittalStatuses: Immutable.List<SubmittalStatus>(),
  submittals: OrderedMap<SubmittalId, Submittal>(),
  tosLink: '',
  unsavedChanges: false,
  uploadCancelationRequests: List<DocId>(),
  userLicense: new License(),
  userQuery: new UserQuery(),
  users: Immutable.OrderedMap<UserId, User>(),
  viewRFI: new NewRFI(),
  viewEdittedRFI: new NewRFI(),
  visibleMappedSheetVersions: Immutable.Set<SheetId>(),
  websocketObservable: EMPTY,
};

export const AppStateFactory = Immutable.Record(defaultValues, 'AppState');

export type SheetEntry = {
  sheet: Sheet;
  sheetVersion: SheetVersion;
  mappedSheetVersion: MappedSheetVersion;
};

export class AppState extends AppStateFactory {
  getProject(): Project {
    const project = this.projects.get(this.selectedProject, new Project());

    return project;
  }

  getUserRole(userId: string): Roles {
    const { role } = this.roles.get(userId, new Role());

    return role;
  }

  getSelectedConnectedAccount(): ConnectedAccount | undefined {
    if (this && this.connectedAccounts) {
      const account = this.connectedAccounts.find(
        (i) => i.id === this.integrationsConnectedAccountId
      );

      return account;
    }

    return undefined;
  }

  getSheetVersionByPageId(pageId: string): SheetVersion {
    const sheetVersion = this.sheets
      .valueSeq()
      .flatMap((sheet) => sheet.versions)
      .find((version) => version.pageid === pageId);
    return sheetVersion || new SheetVersion();
  }

  getCurrentMappedSheetEntries(): OrderedMap<SheetId, SheetEntry> {
    const sheetEntries = this.sheets.reduce((acc, sheet) => {
      const latestVersion = sheet.versions.last(null);

      if (!latestVersion) {
        return acc;
      }

      return acc.set(latestVersion.pageid, {
        sheet,
        sheetVersion: latestVersion,
      });
    }, OrderedMap<SheetId, { sheet: Sheet; sheetVersion: SheetVersion }>());

    const result: OrderedMap<SheetId, SheetEntry> = this.mappedSheetVersions
      .map((msv, pageid) => {
        const sheetEntry = sheetEntries.get(pageid);

        if (!sheetEntry) {
          return null;
        }

        return {
          mappedSheetVersion: msv,
          sheet: sheetEntry.sheet,
          sheetVersion: sheetEntry.sheetVersion,
        };
      })
      .filter((sheetEntry): sheetEntry is SheetEntry => sheetEntry !== null);

    return result;
  }

  getVisibleSheetEntries(): OrderedMap<SheetId, SheetEntry> {
    return this.getCurrentMappedSheetEntries().filter((_, pageId) =>
      this.visibleMappedSheetVersions.has(pageId)
    );
  }

  // NOTE: This should help in the future to eliminate the type property on Interaction Records.
  hasInteraction(interKey: InteractionConstructor | InteractionKey): boolean {
    const interName = isString(interKey) ? interKey : interKey.displayName;

    return this.interactions.some((ii) => {
      return Record.getDescriptiveName(ii) === interName;
    });
  }

  getInteraction<K extends keyof InteractionsAsMap>(
    interKey: K
  ): InteractionsAsMap[K] | null {
    const maybeInteraction = this.interactions.find(
      (i) => Record.getDescriptiveName(i) === interKey
    );

    return maybeInteraction ? (maybeInteraction as InteractionsAsMap[K]) : null;
  }

  getInteractions<K extends keyof InteractionsAsMap>(
    interKey: K
  ): List<InteractionsAsMap[K]> {
    return this.interactions.filter(
      (i): i is InteractionsAsMap[K] =>
        Record.getDescriptiveName(i) === interKey
    );
  }
}
